import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCw, ScrollText, X } from "lucide-react";
import type { JobRow } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useEventListener } from "../lib/events.tsx";
import { jobTitle } from "../lib/labels.ts";
import { stepSummary } from "../lib/release.ts";
import { Banner, Button, Card, ConfirmButton, ErrorState, ProgressBar, StatusPill } from "./Ui.tsx";
import { LogView } from "./LogView.tsx";

const ACTIVE = new Set(["queued", "claimed", "running"]);
const RETRYABLE = new Set(["failed", "canceled", "expired"]);

/**
 * Uygulama sayfasından başlatılan işi SAYFADAN AYRILMADAN izler. Önceden başlatınca
 * iş sayfasına gidiliyordu; sonraki adım için soldan uygulamaya geri dönmek gerekiyordu.
 * Bitince sonucu özetler ve hattın sonraki adımını tek tıkla açar; patlarsa aynı
 * parametrelerle tekrar denemeyi önerir.
 */
export function RunPanel({
  jobId,
  type,
  next,
  onRetry,
  onClose,
}: {
  jobId: string;
  /** İş yüklenmeden başlık boş kalmasın diye çağıranın bildiği tip. */
  type?: string;
  /** Hattın sonraki adımı; iş başarıyla bitince düğme olarak çıkar. */
  next?: { title: string; onStart: () => void };
  /** Verilirse başarısız işte "Tekrar dene" çıkar (aynı parametrelerle formu açar). */
  onRetry?: (job: JobRow) => void;
  onClose: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const root = useRef<HTMLDivElement>(null);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Panel hattın altında açılıyor; küçük ekranda görünmeden çalışmasın.
  useEffect(() => {
    root.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  const { data, error, refetch } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => client.job(jobId),
    // Olay akışı yedeği: iş sürerken durum yine de yoklanır.
    refetchInterval: (q) => (q.state.data && !ACTIVE.has(q.state.data.job.status) ? false : 3000),
  });

  useEventListener(
    (event) => {
      if ((event.t === "job.update" || event.t === "process.status") && event.jobId === jobId) {
        void queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      }
    },
    [jobId],
  );

  async function cancel(): Promise<void> {
    setCanceling(true);
    setCancelError(null);
    try {
      await client.cancelJob(jobId);
      await queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    } catch (err) {
      setCancelError(humanizeError(err, "İptal edilemedi"));
    } finally {
      setCanceling(false);
    }
  }

  const job = data?.job;
  const active = job ? ACTIVE.has(job.status) : !error;
  const summary = job ? stepSummary(job) : undefined;
  const title = jobTitle(job?.type ?? type ?? "iş");

  return (
    <div ref={root} className="scroll-mt-4">
      <Card
        title={title}
        action={
          <>
            <Link to={`/jobs/${jobId}`} className="btn btn-ghost btn-sm">
              <ScrollText size={14} strokeWidth={1.9} aria-hidden />
              Tam log
            </Link>
            {job && active ? (
              <ConfirmButton size="sm" loading={canceling} onConfirm={cancel} confirmLabel="İptal edilsin mi?">
                İptal et
              </ConfirmButton>
            ) : (
              <Button size="sm" icon={X} onClick={onClose}>
                Kapat
              </Button>
            )}
          </>
        }
        flush
        className="overflow-hidden"
      >
        {error && !job ? (
          <ErrorState error={error} onRetry={() => void refetch()} compact />
        ) : (
          <>
            <div
              role="status"
              aria-live="polite"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-neutral-900 px-4 py-2.5 text-xs text-neutral-400"
            >
              {job ? <StatusPill status={job.status} /> : <StatusPill status="queued" />}
              <span className="min-w-0 flex-1 break-words">
                {active ? (job?.step ?? "başlatılıyor…") : (summary ?? "")}
              </span>
            </div>
            {active && <ProgressBar pct={job?.pct} />}
          </>
        )}

        {cancelError && (
          <div className="px-4 pt-3">
            <Banner tone="danger">{cancelError}</Banner>
          </div>
        )}

        {job && RETRYABLE.has(job.status) && (
          <div className="px-4 py-3">
            <Banner
              tone="danger"
              action={
                onRetry && (
                  <Button size="sm" variant="primary" icon={RotateCw} onClick={() => onRetry(job)}>
                    Tekrar dene
                  </Button>
                )
              }
            >
              {humanizeError(
                job.error,
                job.status === "canceled"
                  ? "İş iptal edildi."
                  : job.status === "expired"
                    ? "İş zamanında alınamadı (agent kapalı olabilir)."
                    : "İş başarısız oldu.",
              )}
            </Banner>
          </div>
        )}

        {job?.status === "succeeded" && next && (
          <div className="flex flex-wrap items-center gap-3 border-b border-neutral-900 px-4 py-3">
            <span className="text-sm text-ok">✓ Tamamlandı{summary ? ` · ${summary}` : ""}</span>
            <Button variant="primary" icon={ArrowRight} className="ml-auto" onClick={next.onStart}>
              Sonraki adım: {next.title}
            </Button>
          </div>
        )}
        <LogView jobId={jobId} active={active} height={260} />
      </Card>
    </div>
  );
}
